import { useCallback, useRef, useEffect, useState } from 'react';
import SignaturePad from 'react-signature-canvas';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

const Signature = ({ getSignature, DefaultUrl = null }) => {
  const signatureCanvasRef = useRef(null);
  const [signatureData, setSignatureData] = useState({
    imageURL: DefaultUrl,
    file: null
  });

  // Handle canvas disable/enable based on existing signature
  useEffect(() => {
    const signaturePad = signatureCanvasRef.current;
    if (signaturePad) {
      if (signatureData?.imageURL) {
        signaturePad.off();
      } else {
        signaturePad.on();
      }
    }
  }, [signatureData?.imageURL]);

  const createFileFromBlob = useCallback(async (blob) => {
    const filename = `signature_${Date.now()}_${Math.random()
      .toString(36)
      .substring(7)}.png`;
    return new File([blob], filename, { type: 'image/png' });
  }, []);

  const convertDataURLtoFile = useCallback(
    async (dataURL) => {
      try {
        const response = await fetch(dataURL);
        const blob = await response.blob();
        return await createFileFromBlob(blob);
      } catch (error) {
        console.error('Error converting signature to file:', error);
        return null;
      }
    },
    [createFileFromBlob]
  );

  const saveSignature = useCallback(async () => {
    const signaturePad = signatureCanvasRef.current;
    if (!signaturePad || signaturePad.isEmpty()) {
      setSignatureData({ imageURL: null, file: null });
      return null;
    }
    try {
      const dataURL = signaturePad.toDataURL('image/png');
      const file = await convertDataURLtoFile(dataURL);

      setSignatureData({ imageURL: dataURL, file });
      getSignature({ imageURL: dataURL, file });
      return file;
    } catch (error) {
      console.error('Error saving signature:', error);
      return null;
    }
  }, [convertDataURLtoFile, setSignatureData]);

  const clearSignature = useCallback(() => {
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.clear();
      setSignatureData({ imageURL: null, file: null });
    }
  }, [setSignatureData]);

  return (
    <Box>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          position: 'relative'
        }}
      >
        <Typography variant="h6" component="h2">
          Signature Pad
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            border: '1px solid',
            borderColor: signatureData?.imageURL ? 'grey.400' : 'grey.300',
            bgcolor: signatureData?.imageURL ? 'grey.100' : '#fff',
            position: 'relative',
            '& canvas': {
              width: '100% !important',
              height: '200px !important'
            }
          }}
        >
          {signatureData?.imageURL && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                component="img"
                src={signatureData.imageURL}
                alt="Signature"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
          <SignaturePad
            ref={signatureCanvasRef}
            canvasProps={{
              className: 'signature-canvas'
            }}
            penColor="#003087"
            backgroundColor={signatureData?.imageURL ? '#f5f5f5' : '#fff'}
          />
        </Paper>

        <Stack direction="row" spacing={2} sx={{ mt: 2, ml: 'auto' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveSignature}
            disabled={signatureData?.imageURL}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={clearSignature}
            color="error"
          >
            Clear
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Signature;
